import React, { useState, useEffect, useCallback } from 'react'
import { Helmet } from 'react-helmet-async'
import { useInspectorRef } from '../../hooks/useInspectorRef'
import { Alert, Box, Stack } from '@mui/material'

import { apiClient } from '../../services/api'
import { logger } from '../../utils/logger'
import { useInspector } from '../../contexts/InspectorContext'
import type { FranchisePartnerResource } from '../../types/resources'
import {
  DashboardHero,
  type HeroSystems,
} from '../../components/inspector/dashboard/DashboardHero'
import { HealthSummaryTile } from '../../components/inspector/dashboard/SummaryCards'
import type { DomainScore } from '../../components/inspector/dashboard/healthScore'
import { AttentionPanel } from '../../components/inspector/dashboard/AttentionPanel'
import { AuditToolsGrid } from '../../components/inspector/dashboard/AuditToolsGrid'
import { QuickAccessRail } from '../../components/inspector/dashboard/QuickAccessRail'
import { FpEngagementCard } from '../../components/inspector/dashboard/FpEngagementCard'

interface InspectorDashboardData {
  fp_code: string
  franchise_name: string
  country: string
  state: string
  is_active: boolean
  field_management_system: string
  lightspeed_store_code?: string
  xero_short_code?: string
  xero_org_name?: string
  resources?: FranchisePartnerResource[]
  last_validation_run?: string
  last_sync_date?: string
  health_status: Record<string, string>
  health_scores?: Record<string, DomainScore>
  exception_counts?: Record<string, Record<string, number>>
}

interface SyncCountsResponse {
  fp_code: string
  last_updated: string
  systems: HeroSystems
}

const InspectorDashboard: React.FC = () => {
  const { fpCode: fp_code } = useInspectorRef()
  const { franchiseData } = useInspector()
  const [data, setData] = useState<InspectorDashboardData | null>(null)
  const [systems, setSystems] = useState<HeroSystems | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const franchiseName =
    data?.franchise_name || franchiseData?.franchise_name || 'Loading...'
  const pageTitle = `Inspector Tool | ${franchiseName}`

  const fetchDashboardData = useCallback(async () => {
    if (!fp_code) {
      setError('Invalid access parameters')
      setLoading(false)
      return
    }
    try {
      const response = await apiClient.get(`/inspector/dashboard/${fp_code}`)
      setData(response.data)
      setError(null)
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError('🔒 Invalid access token')
      } else if (err.response?.status === 404) {
        setError('🏢 Franchise partner not found or access not enabled')
      } else {
        setError('❌ Failed to load dashboard data')
      }
      logger.error('Error fetching dashboard data:', err)
    }
  }, [fp_code])

  const fetchSyncCounts = useCallback(async () => {
    if (!fp_code) return
    try {
      const response = await apiClient.get<SyncCountsResponse>(
        `/inspector/sync-counts/${fp_code}`,
      )
      setSystems(response.data.systems)
    } catch (err: any) {
      logger.error('Error fetching sync counts:', err)
    }
  }, [fp_code])

  useEffect(() => {
    const run = async () => {
      setLoading(true)
      await Promise.all([fetchDashboardData(), fetchSyncCounts()])
      setLoading(false)
    }
    run()
  }, [fetchDashboardData, fetchSyncCounts])

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    )
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <Stack spacing={3}>
        <DashboardHero
          franchiseName={franchiseName}
          country={data?.country}
          state={data?.state}
          lightspeedStoreCode={data?.lightspeed_store_code}
          xeroShortCode={data?.xero_short_code}
          fieldManagementSystem={data?.field_management_system}
          systems={systems}
          loading={loading}
        />

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          alignItems="flex-start"
        >
          <Box sx={{ flex: { md: 2 }, minWidth: 0, width: '100%' }}>
            <Stack spacing={3}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={3}
                alignItems="stretch"
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <HealthSummaryTile
                    fpCode={fp_code!}
                    healthScores={data?.health_scores}
                    loading={loading}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <AttentionPanel
                    fpCode={fp_code!}
                    healthScores={data?.health_scores}
                    exceptionCounts={data?.exception_counts}
                    loading={loading}
                  />
                </Box>
              </Stack>

              <AuditToolsGrid fpCode={fp_code!} />
            </Stack>
          </Box>

          <Box sx={{ flex: { md: 1 }, minWidth: 0, width: '100%' }}>
            <Stack spacing={3}>
              <QuickAccessRail
                country={data?.country}
                resources={data?.resources}
              />
              <FpEngagementCard fpCode={fp_code!} />
            </Stack>
          </Box>
        </Stack>
      </Stack>
    </>
  )
}

export default InspectorDashboard
